import './App.css';
import GlobalStyle from './App.styled';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { ThemeProvider } from 'styled-components';
import React, { useState, useEffect } from 'react';
import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import { hasRole } from './utils/token';
import { useDispatch, useSelector } from 'react-redux';
import { setDarkMode } from './redux/preference/preferenceSlice';
import { RolesProvider } from './context/rolesContext';
import { lightTheme, darkTheme } from './theme';
import AppShell from './components/shell/appShell';
import LoginScreen from './components/shell/loginScreen';
import SessionGuard from './components/session/sessionGuard';
import Users from './pages/users';
import Home from './pages/home';
import AdvancedSearch from './pages/advancedSearch';
import DashboardPage from './pages/dashboard';
import Unauthorized from './pages/unauthorized';
import DiffPage from './pages/diff';
import AdministrationPage from './pages/administration';
import AdministrationJobsPage from './pages/administrationJobs';
import MigrationPage from './pages/migration';
import AiPage from './pages/aiPage';
import SecurityOverview from './pages/securityOverview';
import SecurityCves from './pages/securityCves';
import SecurityDevices from './pages/securityDevices';
import SecurityApplications from './pages/securityApplications';
import SecurityCompliance from './pages/securityCompliance';

// Ancienne page « Recherche CVE » : on renvoie vers la page Vulnérabilités, en
// reportant le terme recherché sur son paramètre à elle.
function RedirectCveSearch() {
  const [searchParams] = useSearchParams();
  const cve = searchParams.get('q');
  return (
    <Navigate
      to={cve ? `/Security/Vulnerabilities?cve=${encodeURIComponent(cve)}` : '/Security/Vulnerabilities'}
      replace
    />
  );
}

export default function App() {
  const { currentAccessToken } = useSelector((state) => state.auth);
  const { darkMode } = useSelector((state) => state.preference);
  const [roles, setRoles] = useState([]);
  const dispatch = useDispatch();

  // Les rôles applicatifs sont portés par le token d'accès
  useEffect(() => {
    if (currentAccessToken) {
      try {
        const decodedToken = JSON.parse(atob(currentAccessToken.split('.')[1]));
        setRoles(decodedToken.roles || []);
      } catch {
        setRoles([]);
      }
    }
  }, [currentAccessToken]);

  const toggleTheme = () => {
    dispatch(setDarkMode(!darkMode));
  };

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <GlobalStyle />
      <AuthenticatedTemplate>
        <SessionGuard />
        <AppShell roles={roles} darkMode={darkMode} toggleTheme={toggleTheme}>
          <RolesProvider roles={roles}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/Administration" element={ hasRole(roles, [ "Users.Admin" ]) ? <AdministrationPage /> : <Unauthorized /> } />
              <Route path="/Administration/Jobs" element={ hasRole(roles, [ "Users.Admin" ]) ? <AdministrationJobsPage /> : <Unauthorized /> } />
              <Route path="/Users" element={<Users />} />
              <Route path="/AdvancedSearch" element={ hasRole(roles, [ "Users.Admin", "Users.RSSI", "Users.CDS", "Users.Basic" ]) ? <AdvancedSearch /> : <Unauthorized /> } />
              <Route path="/Diff" element={ hasRole(roles, [ "Users.Admin", "Users.RSSI", "Users.CDS" ]) ? <DiffPage /> : <Unauthorized /> } />
              <Route path="/Dashboard" element={ hasRole(roles, [ "Users.Admin", "Users.RSSI", "Users.CDS" ]) ? <DashboardPage /> : <Unauthorized /> } />
              <Route path="/Migration" element={ hasRole(roles, [ "Users.Admin" ]) ? <MigrationPage /> : <Unauthorized /> } />
              <Route path="/AiQuery" element={ hasRole(roles, [ "Users.Admin", "Users.RSSI", "Users.CDS" ]) ? <AiPage /> : <Unauthorized /> } />
              <Route path="/Security" element={ hasRole(roles, [ "Users.Admin", "Users.RSSI", "Users.CDS" ]) ? <SecurityOverview /> : <Unauthorized /> } />
              <Route path="/Security/Vulnerabilities" element={ hasRole(roles, [ "Users.Admin", "Users.RSSI", "Users.CDS" ]) ? <SecurityCves /> : <Unauthorized /> } />
              <Route path="/Security/Search" element={ <RedirectCveSearch /> } />
              <Route path="/Security/Applications" element={ hasRole(roles, [ "Users.Admin", "Users.RSSI", "Users.CDS" ]) ? <SecurityApplications /> : <Unauthorized /> } />
              <Route path="/Security/Compliance" element={ hasRole(roles, [ "Users.Admin", "Users.RSSI", "Users.CDS" ]) ? <SecurityCompliance /> : <Unauthorized /> } />
              <Route path="/Security/Devices" element={ hasRole(roles, [ "Users.Admin", "Users.RSSI", "Users.CDS" ]) ? <SecurityDevices /> : <Unauthorized /> } />
            </Routes>
          </RolesProvider>
        </AppShell>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <LoginScreen />
      </UnauthenticatedTemplate>
    </ThemeProvider>
  );
}
